import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService, type ApproverCandidate } from '../services/userService';

/**
 * Scope of the approval pages: "only my approvals" (people whose direct approver is
 * the logged-in user) or everybody the role can see.
 * Defaults to "mine" when the user has direct reports, otherwise "all"; an explicit
 * choice is remembered per user in localStorage.
 */
export interface ApprovalScope {
  onlyMine: boolean;
  setOnlyMine: (value: boolean) => void;
  directReports: ApproverCandidate[];
  /** False until the direct reports are known, so pages fetch once with the right scope. */
  ready: boolean;
}

export interface ApprovalScopeOptions {
  /** Separate remembered choice (e.g. 'documents'); the approval pages share the unnamed one. */
  namespace?: string;
  /** Overrides the "mine when I have reports" default. */
  defaultOnlyMine?: boolean;
  /** False skips the direct-reports call entirely (ready immediately, no reports). */
  enabled?: boolean;
}

const storageKey = (userId: number, namespace?: string) =>
  namespace ? `approvalScope:${namespace}:${userId}` : `approvalScope:${userId}`;

const readStoredChoice = (userId: number, namespace?: string): boolean | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId, namespace));
    return raw === null ? null : raw === 'mine';
  } catch {
    return null;
  }
};

const writeStoredChoice = (userId: number, onlyMine: boolean, namespace?: string) => {
  try {
    window.localStorage.setItem(storageKey(userId, namespace), onlyMine ? 'mine' : 'all');
  } catch {
    // Private mode or blocked storage: the choice simply lasts for the session.
  }
};

export const useApprovalScope = (options: ApprovalScopeOptions = {}): ApprovalScope => {
  const { namespace, defaultOnlyMine, enabled = true } = options;
  const { user } = useAuth();
  const userId = user?.id;
  const [directReports, setDirectReports] = useState<ApproverCandidate[]>([]);
  const [onlyMine, setOnlyMineState] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;
    if (!enabled) {
      setDirectReports([]);
      setOnlyMineState(false);
      setReady(true);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      let reports: ApproverCandidate[] = [];
      try {
        reports = await userService.getMyDirectReports();
      } catch (error) {
        console.error('Failed to load direct reports:', error);
      }
      if (cancelled) return;
      setDirectReports(reports);
      setOnlyMineState(readStoredChoice(userId, namespace) ?? defaultOnlyMine ?? reports.length > 0);
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, namespace, defaultOnlyMine, enabled]);

  const setOnlyMine = useCallback(
    (value: boolean) => {
      setOnlyMineState(value);
      if (userId) writeStoredChoice(userId, value, namespace);
    },
    [userId, namespace],
  );

  return { onlyMine, setOnlyMine, directReports, ready };
};
